import { Sparkles, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card } from '@/components/ui/card';
import { ToolCard } from '@/components/ToolCard';
import { ResourceCard } from '@/components/ResourceCard';

interface AIResultsProps {
  results: Array<{
    item: any;
    reason: string;
    relevanceScore: number;
  }>;
  appliedFilters: Record<string, any>;
  intent: string;
  totalFound: number;
  type: 'tools' | 'resources';
  onClose: () => void;
}

export function AIResults({ 
  results = [], 
  appliedFilters = {}, 
  intent = '', 
  totalFound = 0, 
  type,
  onClose 
}: AIResultsProps) {
  // Ensure results is always an array
  const safeResults = Array.isArray(results) ? results : [];
  const renderFilterChips = () => {
    const chips: JSX.Element[] = [];
    
    Object.entries(appliedFilters).forEach(([key, values]) => {
      if (Array.isArray(values) && values.length > 0) {
        values.forEach(value => {
          chips.push(
            <Badge key={`${key}-${value}`} variant="secondary" className="bg-accent-light text-accent">
              {value}
            </Badge>
          );
        });
      } else if (typeof values === 'boolean') {
        chips.push(
          <Badge key={key} variant="secondary" className="bg-accent-light text-accent">
            {key === 'ai_enabled' ? (values ? 'AI-powered' : 'Traditional') : String(values)}
          </Badge>
        );
      }
    });

    return chips;
  };

  return (
    <div className="space-y-6 mb-8 animate-fade-in">
      {/* AI Results Header */}
      <Card className="p-6 border-accent/20 bg-gradient-to-br from-accent-light/5 to-accent-light/10 animate-scale-in">
        <div className="flex items-start justify-between mb-4">
          <div className="flex items-center space-x-2">
            <Sparkles className="h-5 w-5 text-accent animate-pulse" />
            <h3 className="font-heading font-semibold text-text-primary">
              AI Curated Results
            </h3>
            <Badge variant="secondary" className="bg-accent-light text-accent text-xs animate-scale-in">
              {safeResults.length} handpicked {type}
            </Badge>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={onClose}
            className="h-6 w-6 p-0 text-text-tertiary hover:text-text-secondary transition-all duration-base hover:scale-110"
          >
            <X className="h-4 w-4" />
          </Button>
        </div>

        <p className="text-sm text-text-secondary mb-4 animate-fade-in">
          {intent}
        </p>

        {/* Applied AI Filters */}
        {Object.keys(appliedFilters).length > 0 && (
          <div className="flex flex-wrap items-center gap-2 animate-fade-in">
            <span className="text-xs text-text-tertiary">AI applied filters:</span>
            {renderFilterChips()}
          </div>
        )}
      </Card>

      {/* AI Results Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 xl:grid-cols-3 gap-6">
        {safeResults.map((result, index) => (
          <div 
            key={index}
            className="animate-fade-in hover-scale"
            style={{ animationDelay: `${index * 100}ms` }}
          >
            {type === 'tools' ? (
              <ToolCard 
                tool={result.item} 
                aiExplanation={{
                  reason: result.reason,
                  relevanceScore: result.relevanceScore
                }}
              />
            ) : (
              <ResourceCard 
                resource={result.item} 
                aiExplanation={{
                  reason: result.reason,
                  relevanceScore: result.relevanceScore
                }}
              />
            )}
          </div>
        ))}
      </div>
    </div>
  );
}